import { useTranslation } from 'react-i18next';

import { Switch } from '~/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

interface ViewBeforeToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Disables the switch — e.g. the platform connection needed for before-data isn't active. */
  disabled?: boolean;
  /** Tooltip shown while disabled, explaining why and how to unblock it. Ignored when not disabled. */
  disabledTooltip?: string;
  /** Automation hook: set on the switch itself; the wrapping label gets `${testId}-label`. */
  testId?: string;
}

/** "View Before" switch shown next to a step's tabs — reveals/hides the before-comparison row on every product-listing table on the page. Defaults to off. */
export function ViewBeforeToggle({
  checked,
  onCheckedChange,
  disabled = false,
  disabledTooltip,
  testId = 'view-before-toggle',
}: ViewBeforeToggleProps) {
  const { t } = useTranslation('content-enrichment');
  const toggle = (
    <label
      data-testid={`${testId}-label`}
      className={cn(
        'flex items-center gap-2 text-sm font-medium',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        data-testid={testId}
      />
      {t('diffTable.viewBefore')}
    </label>
  );

  if (!disabled || !disabledTooltip) return toggle;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{toggle}</TooltipTrigger>
      <TooltipContent>{disabledTooltip}</TooltipContent>
    </Tooltip>
  );
}
